import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  OnInit,
} from '@angular/core';
import { SohoTabsComponent } from 'ids-enterprise-ng';
import { LMDetailSectionBase } from './lm-detail-section-base';
import { LandmarkPanel } from '@form/lm-form.model';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import { LMWorker } from '../../types/lm-worker';
import { LMContextService } from '@services/index';
import { LMCubeDetailSectionPVPeriodEvent, LMListTransactionEvent } from './lm-list.component';
import { Observable } from 'rxjs';
import { LMDetailSectionDashBoardComponent } from './lm-detail-section-dashboard.component';
import { AppConstants } from '@utils/app.constants';
import { LMRefreshRequest } from '@app/components';

export class LMDetailSectionsSaveWorker extends LMWorker {
  readonly detailSections: LMDetailSectionBase[];

  constructor(detailSections: LMDetailSectionBase[]) {
    super();

    this.detailSections = detailSections;
  }

  protected has(i: number): boolean {
    return (i < this.detailSections.length);
  }

  protected get(i: number): LMDetailSectionBase {
    return this.detailSections[ i ];
  }

  protected run(i: number): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const detailSection = this.detailSections[ i ];
      if (detailSection) {
        detailSection.save(observer);
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }
}

@Component({
  selector: 'lm-detail-sections',
  templateUrl: './lm-detail-sections.component.html',
  styleUrls: [ 'lm-detail-sections.component.scss' ]
})
export class LMDetailSectionsComponent implements AfterViewChecked, OnDestroy, OnInit {
  @Input() detailSectionPanels: Array<LandmarkPanel>;
  @Input() public detailSectionIdPrefix: string;
  @Input() public detailSectionAutomationIdPrefix: string;

  @Input() set fromDataView(fromDataView: LMDataView) {
    this.m_fromDataView = fromDataView;
    this.build();
  }
  get fromDataView(): LMDataView {
    return this.m_fromDataView;
  }

  @Input() hideTabs = false;
  @Input() isCardListDetailSections = false;
  @Input() listState: LMListState = {};
  @Input() showSinglePanelTab = false;

  @ViewChildren('detailListComponent') detailListComponents: QueryList<LMDetailSectionBase>;
  @ViewChild('sohoTabsComponent') sohoTabsComponent: SohoTabsComponent;

  @Output() panelContentDisplayed = new EventEmitter<LMContentDisplayedEvent>();
  @Output() sendContextMessageEvent = new EventEmitter<any>();
  @Output() contentModified = new EventEmitter<LMListFieldChangedEvent>();
  @Output() panelDataUpdated = new EventEmitter<LMListTransactionEvent>();
  @Output() uiLinkEvent = new EventEmitter<any>();
  @Output() cubeDetailSectionPVPeriodEvent = new EventEmitter<LMCubeDetailSectionPVPeriodEvent>();
  /**
   * Using null to determine if fromDataView is set or not.
   * Each detail section component needs to handle a null `fromDataView`
   * by disabling itself and removing its current data.
   */
  private m_fromDataView: LMDataView | null = null;
  private m_componentDestroyed = false;
  private activateSinglePanel = false;
  private runHandleResize = false;

  public visiblePanels: Array<LandmarkPanel>;
  public initialBuildComplete = false;
  public testVersion1: boolean;
  public detailSectionAttributes: SohoAttributesArray = [];
  public uiLoadingIndicatorTimeout = 0;

  public readonly tabIdSuffix = AppConstants.ELEMENT_ID_SUFFIX_DETAIL_SECTION_TAB;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmContextService: LMContextService,
  ) {
    this.uiLoadingIndicatorTimeout = this.lmContextService.uiLoadingIndicatorTimeout;
  }

  ngOnInit() {
    this.testVersion1 = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1;
    this.detailSectionAttributes.push({ name: 'data-automation-id', value: 'lm-' + this.detailSectionAutomationIdPrefix + '-tabset' });
    if (!this.testVersion1) {
      this.detailSectionAttributes.push({ name: 'id', value: this.detailSectionIdPrefix + AppConstants.ELEMENT_ID_SUFFIX_DETAIL_SECTION_TABSET });
    }

    this.build();
  }

  ngAfterViewChecked() {
    if (this.activateSinglePanel) {
      // todo uncomment this and this.getVisiblePanelIndex() for april CU
      // const panelIndex = this.getVisiblePanelIndex();

      let panelIndex = 0;
      for (let i = 0; i < this.detailSectionPanels.length; i++) {
        const panel: LandmarkPanel = this.detailSectionPanels[ i ];
        if (!panel.hidden) {
          panelIndex = i;
        }
      }
      this.activateSinglePanel = false;
      setTimeout(() => this.activatePanel(panelIndex), 1);
      return;
    }

    if (this.runHandleResize) {
      if (this.useSohoTabsComponent) {
        setTimeout(() => this.sohoTabsComponent.handleResize());
      }
      this.runHandleResize = false;
    }
  }

  // todo uncomment this and use it above in ngAfterViewChecked,
  //  consider placing this into a service where it can be tested
  // private getVisiblePanelIndex(): number {
  //   const panelIndex = this.detailSectionPanels.findIndex(panel => !panel.hidden);
  //   return panelIndex > -1 ? panelIndex : 0;
  // }

  ngOnDestroy() {
    this.setComponentDestroyed();
  }

  private build() {
    if (this.hasPanels && this.fromDataView) {
      this.updateTabVisibility();
      this.checkSinglePanel();
      this.updateDetailSections();

      if (!this.initialBuildComplete) {
        this.initialBuildComplete = true;
      } else {
        this.runHandleResize = true;
      }
    }
  }

  onTabActivated(event: SohoTabsEvent) {
    setTimeout(() => {
      const panelIndex = event.tab ? this.getTabIndex(event.tab) : this.activePanelIndex;
      this.activatePanel(panelIndex);
    }, 1);
  }

  onPanelDataUpdated(event: LMListTransactionEvent) {
    this.panelDataUpdated.emit(event);
  }

  onDashboardContentDisplayed(event) {
    this.panelContentDisplayed.emit(event);
  }

  onSendContextMessageEvent(event: any) {
    this.sendContextMessageEvent.emit(event);
  }

  onContentModified(event: LMListFieldChangedEvent) {
    this.contentModified.emit(event);
  }

  onDetailSectionDisplayed(event: any) {
    // this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
  }

  onUILinkEvent(event: any) {
    this.uiLinkEvent.emit(event);
  }

  onCubeDetailSectionPVPeriodEvent(event: LMCubeDetailSectionPVPeriodEvent) {
    this.cubeDetailSectionPVPeriodEvent.emit(event);
  }

  trackById(index: number, panel: LandmarkPanel): string {
    return panel.id;
  }

  get hasPanels(): boolean {
    if (this.detailSectionPanels) {
      return this.detailSectionPanels.some(panel => !panel.hidden && panel.isValid);
    }

    return false;
  }

  get useSohoTabsComponent() {
    if (this.showSinglePanelTab) {
      return true;
    }

    this.visiblePanels = this.detailSectionPanels.filter (panel => !panel.hidden && panel.isValid);
    return this.visiblePanels.length > 1;
  }

  public isModified(): boolean {
    return this.detailListComponents.toArray().some(detailSectionComponent =>
      detailSectionComponent.isModified())
  }

  private activatePanel(panelIndex: number) {
    if (this.detailSectionPanels[panelIndex].hidden) {
      // safety - sometimes the timeouts allow for index of 0 to be activated event when it's hidden.
      // this is most notably an issue with soho-tabs where init'ing the tabs.js component causes an
      // activated event of tab '0' before any user input.
      return;
    }

    this.listState.selectedTabIndex = panelIndex;

    const detailSectionPanel = this.detailSectionPanels[panelIndex];
    if (!detailSectionPanel.activated) {
      detailSectionPanel.activated = true;
    } else if (detailSectionPanel.needsReload) {
      detailSectionPanel.needsReload = false;
    }

    if (detailSectionPanel.type === 'dashboard') {
      const detailDashboard = this.detailListComponents.toArray()[panelIndex] as LMDetailSectionDashBoardComponent;
      detailDashboard.refreshDashboard();
    }
  }

  private getTabIndex(tab: HTMLElement): number {
    return Number(tab.dataset.index);
  }

  private get activePanelIndex() {
    if (!this.useSohoTabsComponent) {
      return 0;
    }

    return this.listState.selectedTabIndex;
  }

  private updateDetailSections(): void {
    if (!this.hasPanels) {
      return; // safety
    }

    const index = this.activePanelIndex; // returns 0 if not useSohoTabsComponent === false

    // if (index === -1) {
    //   this.markTabsForReload(index);
    //   return;
    // }

    this.detailSectionPanels[ index ].fromDataView = this.fromDataView;
    if (this.useSohoTabsComponent) {
      this.markTabsForReload(index);
    }
  }

  private checkSinglePanel() {
    if (!this.fromDataView || !this.hasPanels) {
      return;
    }

    if (!this.useSohoTabsComponent) {
      this.activateSinglePanel = true;
    }
  }

  private markTabsForReload(selectedIndex: any) {
    this.detailSectionPanels
      .filter((panel, index) => index !== selectedIndex)
      .forEach(panel => panel.needsReload = panel.activated);
  }

  private updateTabVisibility() {
    if (!this.hasPanels || !this.fromDataView) {
      return;
    }

    // DPB List does this work
    // this.detailSectionPanels.forEach((panel: LandmarkPanel) =>
    //   panel.hidden = (panel.visibility && this.fromDataView) ? !this.lmConditionUtilService.isValid(panel.visibility, this.fromDataView) : false);

    if (this.listState.selectedTabIndex === undefined) {
      this.listState.selectedTabIndex = 0;
    }

    if (this.listState.selectedTabIndex > this.detailSectionPanels.length - 1) {
      this.listState.selectedTabIndex = 0; // safety
    }

    const currentPanel = this.detailSectionPanels[this.listState.selectedTabIndex];
    if (currentPanel.hidden || !currentPanel.isValid ) {
      const nearestPanel = this.findNearestVisibleValidPanel(this.listState.selectedTabIndex);
      if (nearestPanel) {
        if (!this.initialBuildComplete) {
          // ------------------------------------------------------------------------------------
          // if initial build then use template variables to pass in the selection. Otherwise
          // we get a scrollIntoView that causes the screen to get hosed up when detail
          // sections are used with a card list in mobile view. See LMCLIENT-22363 for details
          // ------------------------------------------------------------------------------------
          this.detailSectionPanels.forEach(panel => panel.selected = false);
          nearestPanel.selected = true;
        } else {
          if (this.useSohoTabsComponent) {
            // todo ppatton claudenbach -  concern about setting activated = false. This may
            // causes slight performance degradation due to the datagrid being deconstructed.
            // See LMCLIENT-31795 for details and TCLMRK-4392 for automated testing.
            nearestPanel.activated = false;

            setTimeout(() => this.sohoTabsComponent.select('#' + nearestPanel.id));
          }
        }
      }
    }
  }

  findNearestVisibleValidPanel(selectedIndex: number): LandmarkPanel {

    for (let i = selectedIndex; i > -1; i--) {
      const panel = this.detailSectionPanels[i];
      if (!panel.hidden && panel.isValid) {
        return panel;
      }
    }

    for (let i = selectedIndex, length = this.detailSectionPanels.length; i < length; i++) {
      const panel = this.detailSectionPanels[i];
      if (!panel.hidden && panel.isValid) {
        return panel;
      }
    }

    return null;
  }

  refresh(request?: LMRefreshRequest): Observable<boolean> {
    return new Observable<any>(observer => {
      const detailListComponentsArray = this.detailListComponents.toArray();
      const detailSection = detailListComponentsArray[ this.activePanelIndex ];

      if (detailSection) {
        detailSection.refresh(request).subscribe(() => {
          if (this.useSohoTabsComponent) {
            this.markTabsForReload(this.activePanelIndex);
          }
          observer.next(true);
          observer.complete();
        });
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }

  runSaveAction(): Observable<boolean> {
    const detailListComponentsArray = this.detailListComponents.toArray();
    const modifiedDetailSections = detailListComponentsArray.filter(detailSectionComponent => detailSectionComponent.isModified());

    return new Observable<boolean>(observer => {
      this.lmBusyIndicatorService.showBusyIndicator();
      this.saveDetailSections(modifiedDetailSections).subscribe((continueOn: boolean) => {
        this.lmBusyIndicatorService.hideBusyIndicator();
        observer.next(continueOn);
        observer.complete();
      },
      (err => {
        // let errorPanel: LMDetailSectionBase = err;
        // if (errorPanel) {
        //   this.sohoTabsComponent.select('#' + errorPanel.panelId);
        observer.next(false);
        observer.complete();
      }
      ));

    });
  }

  saveDetailSections(detailSections: LMDetailSectionBase[]): Observable<boolean> {
    const saveWorker: LMDetailSectionsSaveWorker = new LMDetailSectionsSaveWorker(detailSections);
    return saveWorker.start();
  }

  saveDetailSectionsState(): void {
    this.detailListComponents.toArray().forEach(detailListComponent => detailListComponent.saveDetailSectionState());
  }

  clearDetailSectionState(): void {
    this.detailListComponents.toArray().forEach(detailListComponent => detailListComponent.clearDetailSectionState());
  }

  public get componentDestroyed(): boolean {
    return this.m_componentDestroyed;
  }

  protected setComponentDestroyed() {
    this.m_componentDestroyed = true;
  }
}
